import { useRef, type ReactNode } from 'react';
import { BadgeCheck, CreditCard, RotateCcw, type LucideIcon } from 'lucide-react';

import {
  Checklist,
  CostTable,
  EmojiCardGrid,
  FactList,
  FaqList,
  NumberedSteps,
  ProseParagraphs,
} from '@/components/ai-tools/content-sections';
import { ToolCta } from '@/components/ai-tools/tool-cta';
import { ToolHero } from '@/components/ai-tools/tool-hero';
import { ToolInlineCta } from '@/components/ai-tools/tool-inline-cta';
import { ToolSection, ToolSplitSection } from '@/components/ai-tools/tool-section';
import { TrustStrip } from '@/components/ai-tools/trust-strip';
import { JsonLd } from '@/components/directory/json-ld';
import { SecondaryPage } from '@/components/directory/public/secondary-page';
import { envConfigs } from '@/config';
import { localized, type AiToolDefinition } from '@/config/ai-tools';
import { listCreditPacks } from '@/config/pricing';
import { siteConfig } from '@/config/site';
import { Link } from '@/core/i18n/navigation';
import { formatToolDate, formatUsd } from '@/lib/ai-tools/format';
import { buildToolStructuredData } from '@/lib/ai-tools/structured-data';
import { ENTRY_PACK } from '@/lib/ai-tools/studio';
import { track } from '@/lib/track';
import type { SlotEntry } from '@/lib/store/recommendation-slot';
import type { ToolSummary } from '@/modules/directory/types';
import { m } from '@/paraglide/messages.js';
import { getLocale, localizeUrl } from '@/paraglide/runtime.js';
import { HousePromotionBlock } from '@/blocks/house-promotion';
import { RecommendationSlotSection } from '@/blocks/store/recommendation-slot';
import { CreditPacks } from './credit-packs';

type HeroExamples = Parameters<typeof ToolHero>[0]['examples'];

/**
 * The page every /tools generator shares: hero with the studio overlapping its
 * edge, reassurances, a gallery, the trend write-up, the engine-specific
 * options, credit packs, alternatives, FAQ and a closing CTA. An engine page
 * only supplies what differs (its studio, gallery, options and wording), so a
 * change to the conversion path lands on every generator at once.
 */
export function ToolPageFrame({
  tool,
  slot,
  recentRenders,
  heroExamples,
  studio,
  trustItem,
  gallery,
  options,
  ideasTitle,
  relatedTitle,
  costs,
  packsUnit,
  standardCredits,
  cta,
}: {
  tool: AiToolDefinition;
  slot: SlotEntry<ToolSummary>[];
  /** Finished renders over the last week; null while too few to quote. */
  recentRenders: number | null;
  heroExamples: HeroExamples;
  /** The generator; `onNeedCredits` scrolls to the credit packs. */
  studio: (onNeedCredits: () => void) => ReactNode;
  /** The engine-specific third reassurance. */
  trustItem: { icon: LucideIcon; title: string; body: string };
  /** The example gallery, or null when the tool has no examples yet. */
  gallery: { title: string; description: string; content: ReactNode } | null;
  /** The stages / styles section. */
  options: { eyebrow: string; title: string; content: ReactNode };
  ideasTitle: string;
  relatedTitle: string;
  costs: { caption: string; labelHeader: string; valueHeader: string };
  packsUnit: 'video' | 'image';
  standardCredits: number;
  cta: { title: string; description: string; button: string };
}) {
  const locale = getLocale();
  const t = (value: Parameters<typeof localized>[0]) => localized(value, locale);
  const packsRef = useRef<HTMLDivElement>(null);

  const structuredData = buildToolStructuredData({
    tool,
    locale,
    appName: envConfigs.app_name,
    siteUrl: siteConfig.url,
    toolsLabel: m['directory.ai_tools.breadcrumb'](),
    packPricesUsd: listCreditPacks().map((pack) => pack.priceInCents / 100),
    absoluteUrl: (path) => localizeUrl(`${siteConfig.url}${path}`, { locale: locale as 'en' }).href,
  });
  const promotion = (
    <HousePromotionBlock
      placement="tool_page"
      context={{ categories: tool.related.categories, groups: tool.related.groups }}
    />
  );
  const perRender = ENTRY_PACK
    ? formatUsd(Math.round((standardCredits * ENTRY_PACK.priceInCents) / ENTRY_PACK.credits))
    : null;
  const inlineCta = (spot: string) => (
    <ToolInlineCta
      title={m['directory.ai_tools.inline_cta.title']()}
      body={
        perRender
          ? m['directory.ai_tools.inline_cta.body']({ price: perRender })
          : m['directory.ai_tools.inline_cta.body_plain']()
      }
      actionLabel={cta.button}
      actionHref="#studio"
      onAction={() => track('tool_cta_click', { tool: tool.slug, spot })}
    />
  );

  return (
    <SecondaryPage className="space-y-20 pt-6 md:space-y-32">
      {structuredData.map((data, index) => (
        <JsonLd key={index} data={data} />
      ))}

      <div>
        <ToolHero
          overlap
          swatch={tool.cardSwatch}
          breadcrumb={{ parentLabel: m['directory.ai_tools.breadcrumb'](), parentHref: '/tools', current: t(tool.name) }}
          eyebrow={t(tool.eyebrow)}
          title={t(tool.title)}
          subtitle={t(tool.subtitle)}
          examples={heroExamples}
          updatedLabel={m['directory.ai_tools.updated']({ date: formatToolDate(tool.updatedAt, locale) })}
        />
        <div className="relative -mt-20 px-2 sm:-mt-24 sm:px-6 lg:px-10">
          {studio(() => packsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))}
          <div className="mt-12 space-y-6 px-1 sm:px-2">
            {recentRenders !== null ? (
              <p className="text-center text-sm font-medium text-foreground">
                {m['directory.ai_tools.social.recent']({ count: recentRenders.toLocaleString() })}
              </p>
            ) : null}
            <TrustStrip
              items={[
                { icon: RotateCcw, title: m['directory.ai_tools.trust.refund_title'](), body: m['directory.ai_tools.trust.refund_body']() },
                { icon: BadgeCheck, title: m['directory.ai_tools.trust.nosub_title'](), body: m['directory.ai_tools.trust.nosub_body']() },
                trustItem,
                { icon: CreditCard, title: m['directory.ai_tools.trust.secure_title'](), body: m['directory.ai_tools.trust.secure_body']() },
              ]}
            />
          </div>
        </div>
      </div>

      {gallery ? (
        <>
          <ToolSection
            id="examples"
            eyebrow={m['directory.ai_tools.eyebrow.examples']()}
            title={gallery.title}
            description={gallery.description}
          >
            {gallery.content}
          </ToolSection>
          {inlineCta('gallery')}
        </>
      ) : null}

      <ToolSplitSection
        eyebrow={m['directory.ai_tools.eyebrow.trend']()}
        title={t(tool.intro.title)}
        aside={
          <div className="pt-6">
            <FactList
              title={m['directory.ai_tools.facts_title']()}
              items={tool.facts.map((fact) => ({ label: t(fact.label), value: t(fact.value) }))}
            />
          </div>
        }
      >
        <ProseParagraphs paragraphs={tool.intro.paragraphs.map(t)} />
      </ToolSplitSection>

      <ToolSection tone="band" eyebrow={m['directory.ai_tools.eyebrow.how']()} title={m['directory.ai_tools.how_title']()}>
        <NumberedSteps steps={tool.steps.map((step) => ({ title: t(step.title), body: t(step.body) }))} />
      </ToolSection>

      <ToolSection eyebrow={options.eyebrow} title={options.title}>
        {options.content}
      </ToolSection>
      {inlineCta('options')}

      <ToolSection eyebrow={m['directory.ai_tools.eyebrow.ideas']()} title={ideasTitle}>
        <EmojiCardGrid items={tool.ideas.map((idea) => ({ emoji: idea.emoji, title: t(idea.title), body: t(idea.body) }))} />
      </ToolSection>

      <ToolSection tone="band" eyebrow={m['directory.ai_tools.eyebrow.tips']()} title={m['directory.ai_tools.tips_title']()}>
        <Checklist items={tool.photoTips.map(t)} />
      </ToolSection>

      <div ref={packsRef}>
        <ToolSection
          id="credits"
          align="center"
          eyebrow={m['directory.ai_tools.eyebrow.pricing']()}
          title={m['directory.ai_tools.packs.title']()}
          description={m['directory.ai_tools.packs.description']()}
        >
          <div className="space-y-14">
            <CreditPacks
              slug={tool.slug}
              returnPath={`/tools/${tool.slug}?credits=added#studio`}
              standardCredits={standardCredits}
              unit={packsUnit}
            />
            <CostTable
              caption={costs.caption}
              headers={{ label: costs.labelHeader, value: costs.valueHeader }}
              rows={tool.qualities.map((quality) => ({ id: quality.id, label: t(quality.label), value: quality.credits }))}
            />
          </div>
        </ToolSection>
      </div>

      {slot.length > 0 ? (
        <ToolSection
          id="alternatives"
          eyebrow={m['directory.ai_tools.eyebrow.related']()}
          title={relatedTitle}
          description={m['directory.ai_tools.related_description']({ app: envConfigs.app_name })}
        >
          <div className="space-y-8">
            <RecommendationSlotSection trendSlug={tool.slug} entries={slot} />
            {promotion}
          </div>
        </ToolSection>
      ) : (
        promotion
      )}

      <ToolSplitSection
        eyebrow={m['directory.ai_tools.eyebrow.faq']()}
        title={m['directory.ai_tools.faq_title']()}
        description={
          <>
            {m['directory.ai_tools.faq_description']()}{' '}
            <Link href="/contact" className="font-medium text-primary hover:underline">
              {m['directory.ai_tools.faq_contact']()}
            </Link>
          </>
        }
      >
        <FaqList items={tool.faqs.map((faq) => ({ q: t(faq.q), a: t(faq.a) }))} />
      </ToolSplitSection>

      <ToolCta
        swatch={tool.cardSwatch}
        title={cta.title}
        description={cta.description}
        actionLabel={cta.button}
        actionHref="#studio"
        onAction={() => track('tool_cta_click', { tool: tool.slug, spot: 'bottom' })}
      />
    </SecondaryPage>
  );
}
